import React, { useState } from 'react';
import { X } from 'lucide-react';

interface RejectionModalProps {
  type: 'id' | 'address';
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

const RejectionModal: React.FC<RejectionModalProps> = ({
  type,
  onConfirm,
  onClose
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const commonReasons = {
    id: [
      'Document is expired',
      'Document is not clearly visible',
      'Document appears to be modified',
      'Information does not match profile',
      'Document type not accepted',
      'Other'
    ],
    address: [
      'Document is expired',
      'Document is not clearly visible',
      'Address does not match profile',
      'Document type not accepted',
      'Document is too old',
      'Other'
    ]
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const reason = selectedReason === 'Other' ? customReason : selectedReason;
    onConfirm(reason);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-semibold">
            Reject {type === 'id' ? 'ID' : 'Address'} Verification
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            {commonReasons[type].map((reason) => (
              <label key={reason} className="flex items-center space-x-3">
                <input
                  type="radio"
                  value={reason}
                  checked={selectedReason === reason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span className="text-gray-700">{reason}</span>
              </label>
            ))}
          </div>

          {selectedReason === 'Other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specify Reason
              </label>
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                required={selectedReason === 'Other'}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Please provide details..."
              />
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedReason || (selectedReason === 'Other' && !customReason)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reject Verification
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RejectionModal;